import { CiCallResult } from '@whelk/const/src/markdown';
import { RewriteItemType } from '@whelk/store/type';
import { chatSendTypeEnum, recommendTypeEnum } from '@whelk/utils/src/track';

import { NegativeReview } from '../common';
import { GenerateImage, StylizedReplyInfo } from '../share';

export const REQUEST_STATUS = {
  PENDING: 0, // 请求中
  SUCCESS: 1, // 成功
  TIMEOUT: 2, // 超时失败
  OTHER_ERROR: 3, // 其他失败
  SENSITIVE: 4, // 第一次请求 直接返回 敏感词
  CONTINUE_LOADING: 6, // 继续生成 状态
  CONTINUESENSITIVE: 12, // 续写返回敏感词
  RE_PULL: 8888, // 前端状态，如果后端没有返回结束标志，重新拉取
} as const;

export const REQUEST_FAIL_STATUS = [
  REQUEST_STATUS.TIMEOUT,
  REQUEST_STATUS.OTHER_ERROR,
  REQUEST_STATUS.SENSITIVE,
  REQUEST_STATUS.CONTINUESENSITIVE,
] as const;

export type RequestFailEnum =
  | typeof REQUEST_STATUS.TIMEOUT
  | typeof REQUEST_STATUS.OTHER_ERROR
  | typeof REQUEST_STATUS.SENSITIVE
  | typeof REQUEST_STATUS.CONTINUESENSITIVE;

export const TTS_REQUEST_STATUS = {
  ONGOING: 1, // 请求中
  SUCCESS: 2, // 成功
  FAIL: 3, // 失败
} as const;

export const CHAT_TRACK_INFO = {
  [REQUEST_STATUS.SUCCESS]: {
    trackId: 'chat_reply_success',
    text: '请求成功',
  },
  [REQUEST_STATUS.TIMEOUT]: {
    trackId: 'chat_reply_fail',
    text: '请求超时',
  },
  [REQUEST_STATUS.OTHER_ERROR]: {
    trackId: 'chat_reply_fail',
    text: '其他错误(模型返回失败/敏感词接口错误/...)',
  },
  [REQUEST_STATUS.SENSITIVE]: {
    trackId: 'chat_reply_fail',
    text: '敏感词',
  },
  [REQUEST_STATUS.CONTINUESENSITIVE]: {
    trackId: 'chat_reply_fail',
    text: '敏感词',
  },
};

export const IS_END = {
  END: 0, // 完全结束
  INTERMEDIT: 1, // 中间状态 不需要care 告知你当前请求还没结束
  NO: 2, // 展示继续生成按钮
} as const;

export const MSG_TYPE = {
  USER: 'user',
  SYSTEM: 'system',
} as const;

export const CHAT_MSG_TYPE = {
  NORMAL: 0, // 普通对话
  VOICE: 5, // 语音
  MULTI_MODAL: 7, // 多模态
  COPILOT: 8, // copilot
  TELMODE: 9, // 电话语音
  TEXT_TO_IMG: 10, // 文生图
  BUBBLE_REPLY: 11, // 气泡回复
  PRODUCT_PRICE_RATIO: 12, // 商品比价
  CI: 13, // 代码解释器
} as const;

export const NOT_SHOW_RETRY_MSG_TYPE = [
  CHAT_MSG_TYPE.BUBBLE_REPLY,
  CHAT_MSG_TYPE.PRODUCT_PRICE_RATIO,
] as const;

export const CHAT_MODEL_STYLE = {
  INIT: 0, // 初始状态
  NORMAL: 1, // 普通模式
  LOADING: 2, // 搜索模式
  SEARCH_PROGRESS: 3, // 搜索中
  SEARCH_SUCCESS: 4, // 搜索 成功
  SEARCH_FAIL: -1, // 搜索 失败
} as const;

// 代码解释器 返回数据类型
const CI_TYPE = {
  TEXT: 1,
  CODE: 2,
  FILE: 3,
  IMG: 4,
} as const;

export const TEXT_SSE_TYPE = {
  SEND: 1,
  MESSAGE: 2,
  URL_STATUS: 5,
  FOLLOW_UP: 4,
  LOADING: 6,
  AUDIO: 7,
  CLOSE: 8,
};

export const URL_LOAD_STATUS = {
  LOADING: 1,
  FAIL: 2,
  SUCCESS: 3,
} as const;

export const MULTI_MODAL_IMG_ERROR_CODE = 1000036;
export const MULTI_MODAL_FILE_ERROR_CODE = 1000037;
// 2024-08-15 没有搜到使用的地方，如果后续还是没有 就把这里删掉
export const MULTI_MODAL_Text_ERROR_CODE = 13;
export const NEED_LOGIN_ERROR_CODE = 1000048;
export const NEED_OPED_NEW_CHAT_CODES = [1000031, 1000042];
export const ROBOT_BAN_CODES = [1000015];
export const SENSITIVE_ERROR_CODES = [
  13, 1200028, 1200029, 1200030, 1200031, 1200032, 1200033, 1200034, 1200035, 1000035, 1000036,
  1000037, 1000050, 1000051, 1000003,
];
// 用户被封禁
export const USER_BANNED_ERROR_CODE = 22;

type RequestStatusEnum = (typeof REQUEST_STATUS)[keyof typeof REQUEST_STATUS];

type MsgTypeEnum = (typeof MSG_TYPE)[keyof typeof MSG_TYPE];

type IsEndEnum = (typeof IS_END)[keyof typeof IS_END];

type TTSRequestStatusEnum = (typeof TTS_REQUEST_STATUS)[keyof typeof TTS_REQUEST_STATUS];

export type ChatMsgTypeEnum = (typeof CHAT_MSG_TYPE)[keyof typeof CHAT_MSG_TYPE];

export type ChatModalStyleEnum = (typeof CHAT_MODEL_STYLE)[keyof typeof CHAT_MODEL_STYLE];

// 2024-08-15 没有搜到使用的地方，如果后续还是没有 就把这里删掉
export type CiTypeEnum = (typeof CI_TYPE)[keyof typeof CI_TYPE];

// 2024-08-15 没有搜到使用的地方，如果后续还是没有 就把这里删掉
export type TextSseTypeEnum = (typeof TEXT_SSE_TYPE)[keyof typeof TEXT_SSE_TYPE];

export type UrlLoadStatusEnum = (typeof URL_LOAD_STATUS)[keyof typeof URL_LOAD_STATUS];

export type CiChatListDetail = Array<CiCodeType | CiIMGType | CiFileType | CiTextType>;

export interface UserMsgCareTips {
  tips: string;
  phoneNumber: string;
}

export interface TextSseSendData {
  type: typeof TEXT_SSE_TYPE.SEND;
  statusInfo: {
    code: number;
    message: string;
  };
  data: {
    sendResult: ChatMessageMeta;
  };
}

// 2024-08-15 没有搜到使用的地方，如果后续还是没有 就把这里删掉
export interface TextSseLoadingData {
  type: typeof TEXT_SSE_TYPE.LOADING;
  statusInfo: {
    code: number;
    message: string;
  };
  data: {
    sendResult: SearchMessageMeta | PaintMessageMeta;
  };
}

export interface TextSseUrlStatusData {
  type: typeof TEXT_SSE_TYPE.URL_STATUS;
  statusInfo: {
    code: number;
    message: string;
  };
  data: {
    urlStatus: UrlStatus;
  };
}

export interface TextSseMessageData {
  type: typeof TEXT_SSE_TYPE.MESSAGE;
  statusInfo: {
    code: number;
    message: string;
  };
  data: {
    messageResult: ChatMessage;
  };
}

export interface LoadingSseMessageData {
  type: typeof TEXT_SSE_TYPE.LOADING;
  statusInfo: {
    code: number;
    message: string;
  };
  data: {
    statusResult: SearchMessageMeta | PaintMessageMeta;
  };
}

interface AudioMessageMeta {
  msgID: string;
  hex: string;
  isLast: boolean;
}

export interface AudioSeeMessageData {
  type: typeof TEXT_SSE_TYPE.AUDIO;
  statusInfo: {
    code: number;
    message: string;
  };
  data: {
    audioResult: AudioMessageMeta;
  };
}

export interface CloseSseMessageData {
  type: typeof TEXT_SSE_TYPE.CLOSE;
}

export type TextSseData =
  | TextSseSendData
  | TextSseMessageData
  | TextSseUrlStatusData
  | LoadingSseMessageData
  | AudioSeeMessageData;

export interface ChatMessageMeta {
  chatID: string;
  chatTitle: string;
  canSearch: boolean;
  systemMsgID: string;
  userMsgID: string;
  fetchInterval: number;
  careTips?: UserMsgCareTips;
  msgContent?: string;
  isPreSendResult: boolean;
  extra?: {
    form?: MultiModalFormItem[];
    longCutCopyWriting?: string;
    warningText?: string;
  };
}

export interface SearchMessageMeta {
  status: string;
  statusCode: number; // 一般是101
  statusTitle: string;
}

export interface PaintMessageMeta {
  status: string;
  statusCode: number; // 一般是102
}

export interface ChatMessage {
  chatID: string;
  msgID: string;
  content: string;
  msgType: MsgTypeEnum;
  parentMsgID: string;
  requestStatus: RequestStatusEnum;
  wordSpreadRate: number;
  fetchInterval: number;
  isEnd: IsEndEnum;
  msgSubType: ChatMsgTypeEnum; // 0 普通消息 7 多模态
  extra: ChatMsgExtra;
  failedText: string;
  followUpQuestionSwitch?: boolean;
  assistantDetail?: CiChatListDetail;
}

export interface CiCodeType {
  detailID: string;
  type: number;
  msgType: typeof CI_TYPE.CODE;
  status: string;
  statusCode: number;
  code_interpreter: {
    input: string;
    outputs: any[];
  };
}

export interface CiIMGType {
  detailID: string;
  type: number;
  msgType: typeof CI_TYPE.IMG;
  status: string;
  statusCode: number;
  image: {
    size: string;
    file_url: string;
  };
}

export interface CiFileType {
  detailID: string;
  type: number;
  msgType: typeof CI_TYPE.FILE;
  status: string;
  statusCode: number;
  title: string;
  file: {
    file_url: string;
  };
}

export interface CiTextType {
  detailID: string;
  type: number;
  msgType: typeof CI_TYPE.TEXT;
  status: string;
  statusCode: number;
  content: string;
  parentDetailID: number;
}

export interface ChatListItem {
  content: string;
  createTime: number;
  msgID: string;
  parentMsgID: string;
  msgType: MsgTypeEnum;
  requestStatus: RequestStatusEnum;
  fetchInterval: number;
  isEnd: IsEndEnum;
  failedText: string;
  msgSubtype: ChatMsgTypeEnum; // 0 普通消息 7 多模态
  careTips?: UserMsgCareTips;
  extra: ChatMsgExtra;
  feadbackType: number;
}

export type ThumbNailType = {
  prefix: string;
  desc: {
    text: string;
    icon?: string;
  }[];
};

export interface NetSearchStatus {
  finalStatus: ChatPollProcessStatusInfo;
  linkDetail: SearchLinks[];
  thumbnail?: ThumbNailType;
}

export interface ChatPollProcessStatusInfo {
  status?: string;
  statusCode: ChatModalStyleEnum;
}

export interface SearchLinks {
  index: string;
  domainName?: string;
  url: string;
  webIcon: string;
  detail: string;
  platform?: string;
  price?: string;
  title?: string;
  publishTs?: number;
}

export interface UrlLoadStatusItem {
  url: string;
  status: UrlLoadStatusEnum;
  hoverCopywriting: string;
  title?: string;
}

export interface UrlStatus {
  copywriting: string;
  urlLoadStatus: UrlLoadStatusItem[];
}

// 信号收集
export interface FeedbackInfo {
  finishOrSkip: boolean;
  info: string;
  action: number;
  feedbackType?: number;
  reaction: { action: number; text: string }[];
}

// 1: 会话反馈 2: 重试反馈
export const FeedbackPopupType = {
  sessionFeedback: 1, // 会话反馈
  retryFeedback: 2, // 重试反馈
};

export type FeedbackStatusInfo = {
  [key in keyof typeof FeedbackPopupType]?: FeedbackInfo;
};

export interface FollowUpQuestion {
  followUpID: string;
  content: string;
}

export interface ChatMsgExtra {
  fileStatus?: number;
  thinkTime: number;
  links?: { slug: string; title: string }[];
  msgTitle: string;
  parsedQuestion?: string[];
  webPageStatus?: number;
  form: MultiModalFormItem[];
  netSearchStatus?: NetSearchStatus; // 当前模式状态
  stylizedReplyList?: StylizedReplyInfo[];
  generateImage?: GenerateImage;
  urlStatus?: UrlStatus; // 用户消息里的链接读取状态
  feedbackStatus?: FeedbackStatusInfo;
  followUpQuestions: FollowUpQuestion[];
  responseType: string;
  replyMsgType?: string;
  longCutCopyWriting?: string;
  scene?: string;
  isUserStopped?: boolean;
  ciCallResults?: CiCallResult[];
  warningText?: string;
}

export interface MultiModalFormItem {
  fileType?: string[] | string;
  fileByte?: number;
  fileID?: string;
  fileUrl?: string;
  name: string;
  status?: FormItemStatusEnum;
  formType: FormItemTypeEnum; // 类型1:文字, 2:图片，3:pdf 4:doc/docx，5:txt
  content: string; // 内容，文字为文字，图片or文件为图片文件名
  path?: string; // 图片oss地址
}

export const FORM_ITEM_TYPE = {
  TEXT: 1,
  IMG: 2,
  FILE: 6,
} as const;

export enum FormItemStatusEnum {
  NORMAL = 0,
  DELETED = 1,
}

export type FormItemTypeEnum = (typeof FORM_ITEM_TYPE)[keyof typeof FORM_ITEM_TYPE];

export interface AddNewChatMata {
  chatID: string;
  chatHistories: Array<ChatListItem>;
}

export interface HistoryListByPageRespDto {
  items: Array<HistoryListItem>;
  limit: number;
  hasMore: boolean;
}

export const HISTORY_ITEM_TYPE = {
  CHAT: 0, // 普通文本对话
  IMAGE: 1, // 图片
  FILE: 2, // 文件
  URL: 3, // 链接
  PHONE: 4, // 电话
};

export type HistoryItemTypeEnum = (typeof HISTORY_ITEM_TYPE)[keyof typeof HISTORY_ITEM_TYPE];

export interface Attachment {
  id: string;
  type: string; // 类型，文件 - file 图片 - image 文本 - text 网页 - link 电话 - voice
  fileID: string; // 文件id
  fileType: string; // 文件类型，pdf, txt,docx
  fileName: string; // 带后缀
  img: string; // 图片地址
  link: string; // 网页地址
  sceneId: string; // 语音场景id
  text: string; // 文本
  title: string;
  content: string;
}

export const FAVORITE_STATUS = {
  FAVORITE: 1,
  UNFAVORITE: 2,
};

export type FavoriteStatusEnum = (typeof FAVORITE_STATUS)[keyof typeof FAVORITE_STATUS];

export interface HistoryListItem {
  chatID: string;
  chatTitle: string;
  canSearch: boolean;
  chatDescription: string;
  createTime: number;
  updateTime: number;
  isTop: boolean;
  isPhoneMode: boolean;
  robotID: number;
  type: HistoryItemTypeEnum;
  attachments: Array<Attachment>;
  abstractContent: string;
  robotName: string;
  favoriteStatus: FavoriteStatusEnum;
  model?: string;
}

export interface GetTargetChatHistoryData {
  characterID: string;
  messages: Array<ChatListItem>;
  title: string;
  canSearch: boolean;
  model?: string;
}

export interface BubbleInfo {
  canCancel: boolean;
  content: string;
  contentType: string;
  endTime: string;
  id: string;
  robotID: string;
  sceneID: number;
  pictureHeight: number;
  pictureWidth: number;
  isShow: boolean;
}

// 联网模式key
export const INTERNET_SEARCH_NAME = 'internet';

export interface GetNativeCommonConfigMeta {
  wordSpreadRate: number;
  serverVersion: string;
  selectMaxCount: number;
  shareQrcodeOpen: boolean;
  mobileQRCodeURL: string;
  chatScene?: number;
  isShowFeedBack: boolean;
  isShowFollowUp: boolean;
  isShowPlayAudio: boolean;
  isAutoPlay: boolean;
  playSpeedLevel: number;
  isNightMode: boolean;
  searchFromNetwork: boolean;
  modelId?: string;
  isDeepThink: boolean;
}
export interface TutorialLinkInfo {
  update_time: string;
  url: string;
}

export enum FeatureType {
  UPLOAD_IMAGE = 'uploadImg',
  SEARCH_MODE = 'searchMode',
}

export enum SectionType {
  Title = 'title',
  Text = 'text',
  List = 'list',
  ListItem = 'listItem',
  Description = 'description',
}

export type ChatModel = {
  value: string;
  label: string;
  description: string;
  img: string;
  disabled_features?: string[];
  max_input: number;
};
export interface ISection {
  type: SectionType;
  content: string;
  children?: ISection[];
}

export interface INotificationLiveModalConfig {
  version: number; // 版本号
  title: string; // 标题
  sections: ISection[]; // 内容
  buttonText: string; // 按钮文本
  buttonLink?: string; // 按钮链接
}

export interface GetWebCommonConfigMeta {
  apkUrl: string;
  regexp_url: string;
  inspoAppUrl: string;
  systemAvatar: string;
  communityQRCodeURL: string;
  extendWithSensitiveWordsTips: string;
  timbres: string; // 音色, 暂时由后端返回一个固定的值
  multimodalConfig: MultiModalUploadConfig;
  enable_phone_mode: boolean; // 电话模式开关
  homepageAndroidDownloadQrcode: string;
  homepageIOSAppStoreQrcode: string;
  discoverSchema: {
    ios: string;
    android: string;
  };
  shareIOSAppStoreQrcode: string;
  shareAndroidDownloadQrcode: string;
  mobileQRCodeURL: string; // 手机端二维码
  wordSpreadRate: number;
  bubble: BubbleInfo[];
  scene: {
    defaultID: number;
  };
  preload_url_config: {
    enable_file_prelaod: boolean; // 文件开关
    support_type: [
      // 文件格式支持 pdf,txt,doc,docx
      'application/pdf',
      'text/plain',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    file_max_size: number; // 文件最大大小 10MB
    file_preload_timeout: number; // 10s
  };
  rewriteConfig: RewriteItemType[];
  rewriteConfigH5: RewriteItemType[];
  negative_reviews_list: NegativeReview;
  beta_mark: boolean;
  tutorial_link: TutorialLinkInfo;
  chat_model_list: ChatModel[];
  chat_model_config: {
    showDeepThink?: boolean;
    defaultModel?: string;
  };
  chat_notification_modal_config: INotificationLiveModalConfig;
  chat_questionnaire_url: string;
}

export interface MultiModalUploadConfig {
  fileSize: number; // 单位 byte
  picSize: number;
}

export interface GetTTSVoiceData {
  fetchInterval: number;
  requestStatus: TTSRequestStatusEnum;
  result: string[];
}

export interface XxDownloadLinks {
  androidDownloadUrl: string;
  androidQrcode: string;
  iOSDownloadUrl: string;
  iOSQrcode: string;
}

export interface RobotListItemInfo extends Pick<RobotInfo, 'avatarInfo' | 'name'> {
  idStr: string;
}

export interface RobotInfo {
  avatarInfo: {
    medium: string;
    small: string;
    large: string;
    oversize: string;
  };
  canTel?: boolean;
  canSearch?: boolean;
  introduce?: string;
  name: string;
  robotID: number;
  robotIDStr: string;
  status?: number;
  showType: number; // 1:公开 2:私密，3:好友
  canShareChat?: boolean;
  supportMultimodal?: MultiModalFormItem[];
  sug?: { sugText: string }[];
  canPopupVoiceWindow: boolean;
}

export interface MultiModalConfigData {
  robotInfo: RobotInfo;
}

export interface RobotInfoList {
  robots: RobotListItemInfo[];
}
export interface RobotVoice {
  voiceID: string;
  voiceName: string;
  previewAudioUrl: string;
}

export interface PolicyInfo {
  accessKeyId: string;
  accessKeySecret: string;
  bucketName: string;
  dir: string;
  endpoint: string;
  expiration: string;
  serverTime: string;
  securityToken: string;
  localTime: string;
}

export interface RobotCustomConfig {
  config: {
    robotNickName: string;
    robotNickNameReviewStatus: number;
    robotVoiceID: string;
    enableCustomConfig: boolean;
    userNickName: string;
    userNickNameReviewStatus: number;
    userProfession: string;
    userProfessionReviewStatus: number;
    userGenderID: string;
    userExtraInfo: string;
    userExtraInfoReviewStatus: number;
  };
  formInfo: {
    voiceList: RobotVoice[];
    userVoiceList?: RobotVoice[];
    genderList: {
      id: string;
      label: string;
    };
  };
}

export interface SideCarRes {
  tagList: {
    tagName: string;
    robotList: RobotInfo[];
  }[];
}

export interface PostUploadSpiderData {
  statusInfo: {
    code: 0;
    httpCode: 0;
    message: string;
    serviceTime: 0;
    requestID: string;
    debugInfo: string;
    serverAlert: 0;
  };
}

export interface ReplyStyle {
  styles?: string[];
  length?: string;
}

export interface RetryBody {
  model?: string;
  chatID: string;
  systemMsgID: string;
  userMsgID: string;
  characterID?: string;
  retryType?: number;
  replyStyle?: ReplyStyle;
  content?: string;
  searchMode?: string;
  isAutoPlay?: number;
  playSpeedLevel?: string;
}

export interface TextStyleData {
  availableOptions?: {
    socialTalk?: {
      styles: {
        id: string;
        name: string;
      }[];
      lengths: {
        id: string;
        name: string;
      }[];
    };
  };
  scene?: {
    socialTalk?: ReplyStyle;
  };
}

export interface SugOption {
  sceneID?: string;
  demoName?: string; // for埋点 sug名称
  selectionOptions?: OptionItem[];
  template?: string;
}

export interface OptionItem {
  optionID?: string;
  optionName?: string;
  optionValues?: string[];
  eventType?: string; // for埋点 动态字段，标识当前选项的埋点key值
}

interface FileItem {
  fileID: string;
  formType: number; // 1 文字 2:图片，6:文件
  content: string;
  path: string;
  fileType: string;
  width: number;
  height: number;
}

// sendMessage jsb bridge
export interface SendMessageJsbData {
  msgContent: string;
  recommendType?: string;
  exampleID?: string;
  type: string;
  voiceId?: string;
  msgType?: string;
  fileList?: FileItem[];
  articleInfo?: any;
  isEditLastQuestion?: boolean;
  sugOption?: SugOption;
}

export interface PostSendTextSseExtraData {
  bridgeOriginData?: SendMessageJsbData;
  fileType: string;
  isMultiModal: boolean;
  msgType: chatSendTypeEnum;
  recommendType: recommendTypeEnum;
}
